import { Component, signal, inject, HostListener, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AddEditDialogComponent } from '../add-edit-claim/add-edit-dialog.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { MainGridComponent } from '../../../features/claims/main-grid/main-grid.component';
import { ClaimsService } from '../../../core/services/claims.service';
import { IClaimsDialogData } from '../../interfaces/claims-dialog.model';
import { CLAIMS_THRESHOLDS_VALUES, CLASS_NAME, UI_CONSTANTS } from '../../constants/claims-ui-constants';
import {DELETE_CLAIM_DIALOG_CONFIG } from '../../constants/claims-ui-constants';
import { DialogDataMapperService } from '../../services/dialog-data-mapper.service';
import { ClaimStore } from '../../../core/store/claim-store';
import { GENERIC_MODAL_DIALOG_INPUT_PARAMETERS, GENERIC_MODAL_DIALOG_NOTIFICATION_MESSAGES } from '../../constants/generic-modal-dialog-values.constants';
import { FILE_EXTENSION, FILENAME_FORMAT, CLAIMS_GRID_MESSAGES } from '../../constants/claims-grid-messages.constants';
import { GenericModalDialogComponent } from "../generic-modal-dialog/generic-modal-dialog.component";
import { GenericLoaderComponent } from '../generic-loader/generic-loader.component';
import { GenericLoaderData } from '../../models/generic-loader.model';
import { DatePipe } from '@angular/common';
import { SnackBarService } from '../../../core/services/snack-bar.service';

@Component({
  selector: 'emma-control-bar',
  standalone: true,
  templateUrl: './control-bar.component.html',
  styleUrls: ['./control-bar.component.scss'],
  imports: [GenericModalDialogComponent, GenericLoaderComponent],
})
export class ControlBarComponent {
  public mainGridRef: MainGridComponent | null = null;
  public genericLoaderData: GenericLoaderData = { showLoader: false, loaderMessage: '' };
  public hasSelectedRows = signal<boolean>(false);
  @Input() public rowCount = 0;
  private readonly dialog = inject(MatDialog);
  private readonly claimStore = inject(ClaimStore);
  private readonly dataMapperService = inject(DialogDataMapperService);
  public genericModalDialogNotificationMessageList: Array<string> = [];
  public genericModalDialogConstant = GENERIC_MODAL_DIALOG_INPUT_PARAMETERS;
  public isGenericModalDialogVisible = false;
  public exportFileName = '';
  public isExportToExcelEnabled = false;
  public onSelectionChanged(hasSelection: boolean): void {
    this.hasSelectedRows.set(hasSelection);
  }
   constructor(
    private readonly claimsService: ClaimsService,
    private readonly datePipe: DatePipe,
    private readonly snackBarService: SnackBarService,
  ) {}

  public get canEditClaim(): boolean {
    const selected = this.claimStore.selectedClaims() ?? [];
    return selected.length === 1;
  }

  public onAddClaim(): void {
    const dialogData: IClaimsDialogData = {
      mode: 'add',
      claimData: {
        client: '',
        sfAccountId: '',
        opportunityOwnerEmail: '',
        officeCode: '',
        claimRenewalId: null,
        expiryDate: null,
        mainResearchUserId: '',
        claimWording: '',
        claimFootnote: '',
        counterSignDate: null,
        crmLink: '',
        languageTranslation: ''
      }
    };
    this.dialog.open(AddEditDialogComponent, {
      data: dialogData,
      panelClass: UI_CONSTANTS.DIALOG.PANEL_CLASS
    });
  }

  public onEditClaim(): void {
    const selectedClaims = this.claimStore.selectedClaims() ?? [];
    if (selectedClaims.length === 0) {
      return;
    }

    const selectedClaim = selectedClaims[0];
    const mappedClaim = this.dataMapperService.mapApiClaimToDialogData(selectedClaim);

    const dialogData: IClaimsDialogData = {
      mode: 'edit',
      claimData: mappedClaim
    };

    this.dialog.open(AddEditDialogComponent, {
      data: dialogData,
      panelClass: UI_CONSTANTS.DIALOG.PANEL_CLASS
    });
  }

  public onDeleteClaim(): void {
    if (!this.hasSelectedRows() || !this.mainGridRef) {
      return;
    }

    const selectedClaims = this.claimStore.selectedClaims() ?? [];
    if (selectedClaims.length === 0) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, DELETE_CLAIM_DIALOG_CONFIG);

    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'delete' && this.mainGridRef) {
        this.performDelete(selectedClaims);
      }
    });
  }

  public isDeleteDisabled(): boolean {
    return !this.hasSelectedRows();
  }

  private performDelete(claimsToDelete: Array<any>): void {

    const claimIds = claimsToDelete
      .filter(claim => claim.claimId)
      .map(claim => claim.claimId);

    if (claimIds.length > 0) {
      this.claimsService.deleteClaims(claimIds).subscribe({
        next: () => {
          this.mainGridRef?.deleteSelectedRows();
        },
      });
    }
  }
  @HostListener('document:claims.show-export-to-excel-popup')
  public onExportButtonClick(): void {
    this.genericModalDialogNotificationMessageList = [];
    const isInValidCellsPresent = document?.querySelectorAll('.e-invalid-value');
    const isAnyFilterPresent = this.mainGridRef?.gridOptions?.api?.isAnyFilterPresent?.();
    if (isInValidCellsPresent?.length && isAnyFilterPresent) {
      this.genericModalDialogNotificationMessageList.push(
        GENERIC_MODAL_DIALOG_NOTIFICATION_MESSAGES.modalDialogExportToExcelFilteredandInvalidRows
      );
    } else if (isInValidCellsPresent?.length) {
      this.genericModalDialogNotificationMessageList.push(
        GENERIC_MODAL_DIALOG_NOTIFICATION_MESSAGES.modalDialogExportToExcelInvalidRows
      );
    } else if (isAnyFilterPresent) {
      this.genericModalDialogNotificationMessageList.push(
        GENERIC_MODAL_DIALOG_NOTIFICATION_MESSAGES.modalDialogExportToExcelFilteredRows
      );
    } else {
      this.genericModalDialogNotificationMessageList = [];
    }
    this.exportFileName = this.getDefaultFileNameForExportToExcel();
    this.isGenericModalDialogVisible = true;
  }

  public onGenericModalDialogSubmitClick(event: { isGenericModalDialogVisible: boolean; exportFileName: string }): void {
    try {
      this.isGenericModalDialogVisible = event.isGenericModalDialogVisible;
      this.exportFileName = event.exportFileName.trim();
      if (this.mainGridRef && typeof this.mainGridRef.onExport === 'function') {
        this.mainGridRef.onExport(this.exportFileName);
      }
    } catch (error) {
      this.snackBarService?.showSnackBar?.(
        CLAIMS_GRID_MESSAGES.exportToExcelSystemErrorsMessage,
        CLAIMS_THRESHOLDS_VALUES.showExportMessagesForTimeDurationInMilliSeconds,
        CLASS_NAME.errors
      );
    }
  }

  public getDefaultFileNameForExportToExcel(): string {
    let fileName = FILENAME_FORMAT.exportToExcelDefaultFileName;
    const projectCode = localStorage.getItem('projectCode') ?? '';
    const timestamp = this.datePipe.transform(new Date(), 'yyyy-MM-dd-HHmmss') ?? '';
    fileName = fileName.replace('{projectCode}', projectCode);
    fileName = fileName.replace('{timestamp}', timestamp);
    fileName = fileName + FILE_EXTENSION.xlsx;
    return fileName.trim().replace(' ', '-');
  }

  public onGenericModalDialogCancelClick(event: { isGenericModalDialogVisible: boolean }): void {
      this.isGenericModalDialogVisible = event.isGenericModalDialogVisible;
  }

   public setExportToExcelFeature(isEnabled: boolean): void {
      this.isExportToExcelEnabled = isEnabled;
  }
}
